/**
 * Knowledge Service
 *
 * API service module for the knowledge base (document upload, processing, retrieval).
 */

import { apiClient, extractData } from './api';

// ─── Types ──────────────────────────────────────────────────────────────────

export type DocumentStatus =
  | 'pending'
  | 'processing'
  | 'indexed'
  | 'failed'
  | 'archived'
  | 'reindexing';

export type DocumentFileType =
  | 'pdf'
  | 'docx'
  | 'pptx'
  | 'csv'
  | 'html'
  | 'txt'
  | 'md'
  | 'xlsx'
  | 'json';

export type DocumentSourceType = 'upload' | 'url' | 'sitemap' | 'google_drive' | 'onedrive';

export interface KnowledgeDocumentUploader {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
}

export interface KnowledgeDocument {
  id: string;
  workspaceId: string;
  title: string;
  description?: string | null;
  status: DocumentStatus;
  fileType: DocumentFileType;
  sourceType: DocumentSourceType;
  fileName?: string | null;
  fileSize?: string | null;
  sourceUrl?: string | null;
  language: string;
  tags: string[];
  version: number;
  chunkCount: number;
  totalTokens: number;
  errorMessage?: string | null;
  lastIndexedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  uploadedBy: KnowledgeDocumentUploader | null;
  processingMetadata?: Record<string, unknown> | null;
}

export interface VersionHistoryEntry {
  version: number;
  changeType: 'metadata_update' | 'reindex' | 'reindex_url' | 'initial';
  changedBy: string | null;
  changedAt: string;
  changes: Record<string, unknown>;
  chunkCount?: number;
  totalTokens?: number;
  contentHash?: string | null;
}

export interface KnowledgeDocumentDetail extends KnowledgeDocument {
  filePath?: string | null;
  contentHash?: string | null;
  approvalStatus?: string;
  expiresAt?: string | null;
  approvedBy?: KnowledgeDocumentUploader | null;
  versionHistory?: VersionHistoryEntry[];
}

export interface KnowledgeChunk {
  id: string;
  documentId: string;
  chunkIndex: number;
  content: string;
  pageNumber: number | null;
  sectionTitle: string | null;
  tokenCount: number | null;
  embeddingId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface KnowledgeDocumentsSummary {
  totalDocuments: number;
  statusDistribution: Record<string, number>;
  fileTypeDistribution: Record<string, number>;
}

export interface ListDocumentsParams {
  workspaceId: string;
  page?: number;
  limit?: number;
  search?: string;
  status?: DocumentStatus;
  fileType?: DocumentFileType;
  sourceType?: DocumentSourceType;
  sortBy?: 'createdAt' | 'title' | 'status' | 'fileType';
  sortOrder?: 'asc' | 'desc';
}

export interface ListDocumentsResponse {
  documents: KnowledgeDocument[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  summary: KnowledgeDocumentsSummary;
}

export interface ListChunksParams {
  workspaceId: string;
  documentId: string;
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: 'chunkIndex' | 'pageNumber' | 'tokenCount' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

export interface ListChunksResponse {
  chunks: KnowledgeChunk[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface UploadDocumentRequest {
  file: File;
  title?: string;
  description?: string;
  tags?: string[];
}

export interface AddUrlDocumentRequest {
  sourceUrl: string;
  title?: string;
  description?: string;
  tags?: string[];
}

export interface CrawlSingleUrlRequest {
  sourceUrl: string;
  title?: string;
  description?: string;
  tags?: string[];
}

export interface CrawlSitemapRequest {
  sourceUrl: string;
  maxDepth?: number;
  respectRobotsTxt?: boolean;
  tags?: string[];
}

export interface CrawlResponse {
  id: string;
  workspaceId: string;
  title: string;
  status: string;
  sourceType: string;
  sourceUrl: string;
  message: string;
  /** For sitemap crawls, the number of URLs discovered */
  pageCount?: number;
}

export interface UpdateDocumentRequest {
  title?: string;
  description?: string | null;
  tags?: string[];
}

export interface UpdateDocumentResponse {
  id: string;
  title: string;
  description: string | null;
  tags: string[];
  version: number;
  updatedAt: string;
}

export interface ReindexResponse {
  id: string;
  status: string;
  version: number;
  message: string;
}

export interface BulkReindexRequest {
  documentIds: string[];
}

export interface BulkReindexResponse {
  queued: number;
  skipped: number;
  results: {
    documentId: string;
    status: 'queued' | 'skipped';
    reason?: string;
  }[];
}

export interface DeleteDocumentResponse {
  id: string;
  title: string;
  message: string;
}

export interface BulkDeleteRequest {
  documentIds: string[];
}

export interface BulkDeleteResponse {
  deleted: number;
  skipped: number;
  results: {
    documentId: string;
    status: 'deleted' | 'skipped';
    reason?: string;
  }[];
}

// ─── Search Types ───────────────────────────────────────────────────────────

export interface SearchKnowledgeParams {
  workspaceId: string;
  query: string;
  page?: number;
  limit?: number;
  minScore?: number;
  status?: DocumentStatus;
  fileType?: DocumentFileType;
  sourceType?: DocumentSourceType;
}

export interface SearchResultChunk {
  chunkId: string;
  content: string;
  chunkIndex: number;
  sectionTitle: string | null;
  pageNumber: number | null;
  tokenCount: number | null;
  relevanceScore: number;
  matchType: string;
}

export interface SearchResultDocument {
  documentId: string;
  title: string;
  description: string | null;
  status: string;
  fileType: string;
  sourceType: string;
  fileName: string | null;
  sourceUrl: string | null;
  tags: unknown;
  version: number;
  uploadedBy: KnowledgeDocumentUploader | null;
}

export interface SearchResult {
  chunk: SearchResultChunk;
  document: SearchResultDocument;
}

export interface SearchKnowledgeResponse {
  results: SearchResult[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  query: string;
}

// ─── Knowledge Service ──────────────────────────────────────────────────────

export const knowledgeService = {
  /**
   * List knowledge documents for a workspace.
   */
  list: async (params: ListDocumentsParams): Promise<ListDocumentsResponse> => {
    const response = await apiClient.get(
      `/workspaces/${params.workspaceId}/knowledge-documents`,
      {
        params: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          status: params.status,
          fileType: params.fileType,
          sourceType: params.sourceType,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
        },
      }
    );

    const body = response.data as {
      success: boolean;
      data: KnowledgeDocument[];
      meta?: {
        pagination?: ListDocumentsResponse['pagination'];
        summary?: KnowledgeDocumentsSummary;
      };
    };

    return {
      documents: body.data || [],
      pagination: body.meta?.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPrevPage: false,
      },
      summary: body.meta?.summary || {
        totalDocuments: 0,
        statusDistribution: {},
        fileTypeDistribution: {},
      },
    };
  },

  /**
   * Get a single knowledge document by ID with full details.
   */
  getById: async (workspaceId: string, documentId: string): Promise<KnowledgeDocumentDetail> => {
    const response = await apiClient.get(
      `/workspaces/${workspaceId}/knowledge-documents/${documentId}`
    );

    const body = response.data as {
      success: boolean;
      data: KnowledgeDocumentDetail;
    };

    return body.data;
  },

  /**
   * List paginated chunks for a knowledge document.
   */
  listChunks: async (params: ListChunksParams): Promise<ListChunksResponse> => {
    const response = await apiClient.get(
      `/workspaces/${params.workspaceId}/knowledge-documents/${params.documentId}/chunks`,
      {
        params: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
        },
      }
    );

    const body = response.data as {
      success: boolean;
      data: KnowledgeChunk[];
      meta?: {
        pagination?: ListChunksResponse['pagination'];
      };
    };

    return {
      chunks: body.data || [],
      pagination: body.meta?.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPrevPage: false,
      },
    };
  },

  /**
   * Update a knowledge document's metadata (title, description, tags).
   */
  update: async (
    workspaceId: string,
    documentId: string,
    data: UpdateDocumentRequest
  ): Promise<UpdateDocumentResponse> => {
    return extractData(
      apiClient.put<UpdateDocumentResponse>(
        `/workspaces/${workspaceId}/knowledge-documents/${documentId}`,
        data
      )
    );
  },

  /**
   * Upload a new document to the knowledge base.
   */
  upload: (workspaceId: string, data: UploadDocumentRequest): Promise<KnowledgeDocument> => {
    const formData = new FormData();
    formData.append('file', data.file);
    if (data.title) formData.append('title', data.title);
    if (data.description) formData.append('description', data.description);
    if (data.tags) formData.append('tags', JSON.stringify(data.tags));
    return extractData(
      apiClient.post<KnowledgeDocument>(`/workspaces/${workspaceId}/knowledge-documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    );
  },

  /**
   * Upload a document with progress tracking.
   * Returns the created KnowledgeDocument.
   */
  uploadWithProgress: (
    workspaceId: string,
    data: UploadDocumentRequest,
    onProgress?: (percent: number) => void
  ): Promise<KnowledgeDocument> => {
    const formData = new FormData();
    formData.append('file', data.file);
    if (data.title) formData.append('title', data.title);
    if (data.description) formData.append('description', data.description);
    if (data.tags) formData.append('tags', JSON.stringify(data.tags));

    return extractData(
      apiClient.post<KnowledgeDocument>(
        `/workspaces/${workspaceId}/knowledge-documents/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total) {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress(Math.min(percent, 99)); // Cap at 99 until server responds
            }
          },
        }
      )
    );
  },

  /**
   * Get existing tags used across knowledge documents in a workspace.
   */
  getTags: async (workspaceId: string): Promise<string[]> => {
    const response = await apiClient.get(
      `/workspaces/${workspaceId}/knowledge-documents/tags`
    );

    const body = response.data as {
      success: boolean;
      data: string[];
    };

    return body.data || [];
  },

  /**
   * Trigger re-indexing of a document.
   */
  reindex: async (workspaceId: string, documentId: string): Promise<ReindexResponse> => {
    return extractData(
      apiClient.post<ReindexResponse>(
        `/workspaces/${workspaceId}/knowledge-documents/${documentId}/reindex`
      )
    );
  },

  /**
   * Trigger bulk re-indexing of multiple documents.
   */
  bulkReindex: async (
    workspaceId: string,
    data: BulkReindexRequest
  ): Promise<BulkReindexResponse> => {
    return extractData(
      apiClient.post<BulkReindexResponse>(
        `/workspaces/${workspaceId}/knowledge-documents/bulk-reindex`,
        data
      )
    );
  },

  /**
   * Crawl a single URL and add it to the knowledge base.
   */
  crawlUrl: async (workspaceId: string, data: CrawlSingleUrlRequest): Promise<CrawlResponse> => {
    return extractData(
      apiClient.post<CrawlResponse>(
        `/workspaces/${workspaceId}/knowledge-documents/crawl`,
        {
          sourceUrl: data.sourceUrl,
          crawlType: 'url',
          title: data.title,
          description: data.description,
          tags: data.tags,
        }
      )
    );
  },

  /**
   * Crawl a sitemap and add discovered pages to the knowledge base.
   */
  crawlSitemap: async (workspaceId: string, data: CrawlSitemapRequest): Promise<CrawlResponse> => {
    return extractData(
      apiClient.post<CrawlResponse>(
        `/workspaces/${workspaceId}/knowledge-documents/crawl`,
        {
          sourceUrl: data.sourceUrl,
          crawlType: 'sitemap',
          maxDepth: data.maxDepth ?? 2,
          respectRobotsTxt: data.respectRobotsTxt ?? true,
          tags: data.tags,
        }
      )
    );
  },

  /**
   * Add a document from a URL source.
   */
  addUrl: (workspaceId: string, data: AddUrlDocumentRequest): Promise<KnowledgeDocument> =>
    extractData(
      apiClient.post<KnowledgeDocument>(`/workspaces/${workspaceId}/knowledge-documents/url`, data)
    ),

  /**
   * Delete a knowledge document.
   */
  delete: async (workspaceId: string, documentId: string): Promise<DeleteDocumentResponse> => {
    return extractData(
      apiClient.delete<DeleteDocumentResponse>(
        `/workspaces/${workspaceId}/knowledge-documents/${documentId}`
      )
    );
  },

  /**
   * Delete multiple knowledge documents in bulk.
   */
  bulkDelete: async (
    workspaceId: string,
    data: BulkDeleteRequest
  ): Promise<BulkDeleteResponse> => {
    return extractData(
      apiClient.post<BulkDeleteResponse>(
        `/workspaces/${workspaceId}/knowledge-documents/bulk-delete`,
        data
      )
    );
  },

  /**
   * Search the knowledge base using hybrid vector + full-text search.
   */
  search: async (params: SearchKnowledgeParams): Promise<SearchKnowledgeResponse> => {
    const response = await apiClient.get(
      `/workspaces/${params.workspaceId}/knowledge-documents/search`,
      {
        params: {
          q: params.query,
          page: params.page,
          limit: params.limit,
          minScore: params.minScore,
          status: params.status,
          fileType: params.fileType,
          sourceType: params.sourceType,
        },
      }
    );

    const body = response.data as {
      success: boolean;
      data: SearchKnowledgeResponse;
    };

    return body.data;
  },
};